/**
 * Ba biểu đồ của thẻ «Tình hình kinh doanh» cho nút đang xem: doanh thu thực tế cộng
 * dồn theo ngày cạnh đường mục tiêu, xu hướng theo tháng, doanh thu dự kiến theo nguồn.
 */
import { Bar, BarChart, CartesianGrid, Legend, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";
import { ChartFrame } from "@/components/ChartFrame";
import { ChartCard } from "@/features/dashboard/primitives/ChartCard";
import { ChartTip } from "@/features/dashboard/primitives/ChartTip";
import { readToken } from "@/features/dashboard/lib/palette";
import { intVN, vnd, vndCompact } from "@/features/dashboard/lib/format";
import type { SalesTeamOverviewNode } from "../api";
import { monthText } from "../labels";
import { cumulativeSeries, sourceLabel } from "../overview";

type Props = { node: SalesTeamOverviewNode; month: string; schemeStart: string };

export function OverviewCharts({ node, month, schemeStart }: Props) {
  const brand = readToken("--color-brand", "#8b6914");
  const muted = readToken("--color-info", "#7ba7c2");
  const series = cumulativeSeries(node, month);
  const hasTarget = series.some((p) => p.target != null);
  const trend = node.trend.map((t) => ({ ...t, label: monthText(t.month) }));
  const sources = node.sources.map((s) => ({ ...s, label: sourceLabel(s.source) }));

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <ChartCard
        title="Doanh thu thực tế cộng dồn trong tháng"
        subtitle={hasTarget ? "Đường nét đứt: nhịp cần đi để đạt mục tiêu" : "Chưa đặt mục tiêu cho phần này"}
        className="lg:col-span-2"
      >
        <ChartFrame height={260}>
          <LineChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="day" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis tickFormatter={vndCompact} tick={{ fontSize: 11 }} width={52} />
            <Tooltip content={<ChartTip formatter={(v) => vnd(v)} />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="actual" name="Thực tế" stroke={brand} strokeWidth={2} dot={false} connectNulls={false} />
            {hasTarget ? (
              <Line type="linear" dataKey="target" name="Mục tiêu" stroke={muted} strokeDasharray="6 4" dot={false} />
            ) : null}
          </LineChart>
        </ChartFrame>
      </ChartCard>

      <ChartCard
        title="Xu hướng theo tháng"
        subtitle={`Tính từ tháng ${monthText(schemeStart.slice(0, 7))} — tối đa 6 tháng gần nhất`}
      >
        <ChartFrame height={240}>
          <BarChart data={trend} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} />
            <YAxis tickFormatter={vndCompact} tick={{ fontSize: 11 }} width={52} />
            <Tooltip content={<ChartTip formatter={(v) => vnd(v)} />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="realRevenue" name="Thực tế" fill={brand} radius={[3, 3, 0, 0]} />
            <Bar dataKey="expected" name="Dự kiến" fill={muted} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ChartFrame>
      </ChartCard>

      <ChartCard title="Doanh thu dự kiến theo nguồn khách" subtitle="Theo nguồn ghi trên đơn">
        {sources.length === 0 ? (
          <p className="py-10 text-center text-sm text-muted-foreground">Chưa có đơn nào có ngày chụp trong tháng.</p>
        ) : (
          <ChartFrame height={Math.max(160, sources.length * 34)}>
            <BarChart data={sources} layout="vertical" margin={{ top: 4, right: 12, left: 4, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tickFormatter={vndCompact} tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} width={96} />
              <Tooltip
                content={
                  <ChartTip
                    formatter={(v) => {
                      const s = sources.find((x) => x.expected === v);
                      return s ? `${vnd(v)} · ${intVN(s.orders)} đơn` : vnd(v);
                    }}
                  />
                }
              />
              <Bar dataKey="expected" name="Dự kiến" fill={brand} radius={[0, 3, 3, 0]} />
            </BarChart>
          </ChartFrame>
        )}
      </ChartCard>
    </div>
  );
}
